import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ChevronRight } from '@opendate-app/setlist';

import { buildZoneSummaries, filterAndSortInventory, simpleInventory } from '../utils/inventoryFilter';
import SeatInventoryRow from './SeatInventoryRow';

const TABS = [
  { key: 'price', label: 'Lowest price' },
  { key: 'best', label: 'Best seats' },
  { key: 'zone', label: 'By zone' },
];

const ROW_LIMIT = 10;

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const CappedList = ({ items, resetKey, renderItem }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [resetKey]);

  const shown = expanded ? items : items.slice(0, ROW_LIMIT);

  return (
    <div className="seat-inventory-list">
      {shown.map(renderItem)}
      {items.length > ROW_LIMIT ? (
        <button type="button" className="seat-inventory-show-more" onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Show less' : 'Show All'}
        </button>
      ) : null}
    </div>
  );
};

CappedList.propTypes = {
  items: PropTypes.array.isRequired,
  resetKey: PropTypes.string.isRequired,
  renderItem: PropTypes.func.isRequired,
};

const SeatInventoryPanel = ({ inventory, tab, qty, maxPrice, zone, colorByZone, allInPricing, simple, onTabChange, onSelectRow, onHoverRow, onZoneSelect, onClearZone, loading }) => {
  const showZonePicker = !simple && tab === 'zone' && !zone;

  return (
    <div className="seat-inventory-panel">
      {simple ? null : (
        <div className="seat-inventory-tabs">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={`seat-inventory-tab${tab === key ? ' is-active' : ''}`}
              onClick={() => onTabChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="seat-inventory-banner">{allInPricing ? 'Prices include fees.' : 'Prices exclude fees.'}</div>
      {loading ? (
        <div className="seat-inventory-skeletons">
          <div className="seat-inventory-skeleton" />
          <div className="seat-inventory-skeleton" />
          <div className="seat-inventory-skeleton" />
        </div>
      ) : showZonePicker ? (
        <CappedList
          items={buildZoneSummaries(inventory, { qty, maxPrice })}
          resetKey="zone-picker"
          renderItem={(z) => (
            <SeatInventoryRow
              key={z.zone}
              row={{
                id: `zone-${z.zone}`,
                zone: z.zone,
                name: z.label,
                sub: z.ga ? z.gaSub : (z.minPrice == null ? 'See price' : `From ${money(z.minPrice)}`),
                price: z.minPrice,
              }}
              color={colorByZone[z.zone]}
              onSelect={() => onZoneSelect(z.zone)}
              actionIcon={ChevronRight}
            />
          )}
        />
      ) : (
        <>
          {zone ? (
            <div className="seat-inventory-zonebar">
              <button type="button" className="seat-inventory-clearzone" onClick={onClearZone}>
                Show all zones
              </button>
            </div>
          ) : null}
          <CappedList
            items={simple ? simpleInventory(inventory, { maxPrice, zone }) : filterAndSortInventory(inventory, { qty, maxPrice, zone, tab })}
            resetKey={`${tab}|${zone || ''}`}
            renderItem={(row) => (
              <SeatInventoryRow key={row.id} row={row} color={colorByZone[row.zone]} onSelect={onSelectRow} onHover={simple ? onHoverRow : undefined} />
            )}
          />
        </>
      )}
    </div>
  );
};

SeatInventoryPanel.propTypes = {
  allInPricing: PropTypes.bool,
  simple: PropTypes.bool,
  inventory: PropTypes.array.isRequired,
  tab: PropTypes.oneOf(['price', 'best', 'zone']).isRequired,
  qty: PropTypes.number.isRequired,
  maxPrice: PropTypes.number.isRequired,
  zone: PropTypes.string,
  colorByZone: PropTypes.object.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onSelectRow: PropTypes.func.isRequired,
  onHoverRow: PropTypes.func,
  onZoneSelect: PropTypes.func.isRequired,
  onClearZone: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

SeatInventoryPanel.defaultProps = {
  zone: null,
  simple: false,
  onHoverRow: undefined,
  loading: false,
};

export default SeatInventoryPanel;
